import { useState, useRef } from 'react';

import { ChevronLeft, ChevronRight, Gift } from 'lucide-react';
import { SmartProductImage } from './SmartProductImage';

// Branded placeholder shown when a product has no images, or when an
// image URL fails to load. Keeps missing imagery looking intentional
// instead of rendering a blank panel.
function ImagePlaceholder({ alt }) {
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center gap-2"
      style={{ background: 'linear-gradient(160deg, #FFF4E6 0%, #FED7A5 140%)' }}
      data-testid="image-placeholder"
      role="img"
      aria-label={alt}
    >
      <Gift className="w-10 h-10" style={{ color: 'rgba(158, 103, 82, 0.45)' }} />
      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: 'rgba(158, 103, 82, 0.55)' }}>
        Mementa
      </span>
    </div>
  );
}

export default function ImageCarousel({ images, alt, className = '', compact = false, showIndicators = true, showCounter = false }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [failedImages, setFailedImages] = useState({});
  
  // Touch/Swipe support
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const minSwipeDistance = 50;
  
  // Ensure images is a clean array (drop null/undefined/empty entries)
  const imageArray = (Array.isArray(images) ? images : [images]).filter(Boolean);
  const hasMultipleImages = imageArray.length > 1;

  const goToPrevious = (e) => {
    if (e) e.stopPropagation();
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === 0 ? imageArray.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 200); // Reduced from 400ms to 200ms
  };

  const goToNext = (e) => {
    if (e) e.stopPropagation();
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === imageArray.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 200); // Reduced from 400ms to 200ms
  };

  const goToIndex = (index, e) => {
    if (e) e.stopPropagation();
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 200); // Reduced from 400ms to 200ms
  };

  // Touch handlers for swipe support
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    e.stopPropagation();
    const swipeDistance = touchStartX.current - touchEndX.current;
    
    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swiped left - go to next
        goToNext();
      } else {
        // Swiped right - go to previous
        goToPrevious();
      }
    }
  };

  // No usable images at all → render the branded placeholder.
  if (imageArray.length === 0) {
    return (
      <div className={`relative w-full h-full overflow-hidden ${className}`}>
        <ImagePlaceholder alt={alt} />
      </div>
    );
  }

  return (
    <div 
      className={`relative w-full h-full overflow-hidden ${className}`}
      onTouchStart={hasMultipleImages ? handleTouchStart : undefined}
      onTouchMove={hasMultipleImages ? handleTouchMove : undefined}
      onTouchEnd={hasMultipleImages ? handleTouchEnd : undefined}
    >
      {/* Images Container with Slide Animation */}
      <div 
        className="relative w-full h-full flex"
        style={{
          transform: `translateX(-${currentIndex * 100}%)`,
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)' // Smoother easing and faster (was 0.4s)
        }}
      >
        {imageArray.map((img, index) => (
          <div 
            key={`carousel-img-${index}-${img}`}
            className="w-full h-full flex-shrink-0"
          >
            {failedImages[index] ? (
              <ImagePlaceholder alt={alt} />
            ) : (
              <SmartProductImage
                src={img}
                alt={`${alt} - Image ${index + 1}`}
                onError={() => setFailedImages((f) => ({ ...f, [index]: true }))}
              />
            )}
          </div>
        ))}
      </div>

      {/* Counter - Bottom Center (for modal) */}
      {hasMultipleImages && showCounter && (
        <div 
          className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm z-10"
          style={{background: 'rgba(0, 0, 0, 0.6)', color: 'white'}}
        >
          {currentIndex + 1} / {imageArray.length}
        </div>
      )}

      {/* Navigation Arrows - Only show on desktop (hidden on mobile with md:flex) */}
      {hasMultipleImages && !compact && showIndicators && (
        <>
          <button
            onClick={goToPrevious}
            className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center transition-all hover:scale-110 hover:bg-white/60 z-10"
            style={{background: 'rgba(255, 255, 255, 0.35)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'}}
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" style={{color: '#20212B'}} />
          </button>

          <button
            onClick={goToNext}
            className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center transition-all hover:scale-110 hover:bg-white/60 z-10"
            style={{background: 'rgba(255, 255, 255, 0.35)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'}}
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" style={{color: '#20212B'}} />
          </button>
        </>
      )}

      {/* Dots Indicator - Only show if showIndicators is true */}
      {hasMultipleImages && showIndicators && !showCounter && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {imageArray.map((img, index) => (
            <button
              key={`indicator-${index}-${img}`}
              onClick={(e) => goToIndex(index, e)}
              className="transition-all duration-300"
              style={{
                width: currentIndex === index ? '20px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: currentIndex === index 
                  ? 'rgba(255, 255, 255, 0.95)' 
                  : 'rgba(255, 255, 255, 0.5)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
